import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import * as clinicsApi from '../../../../apis/clinics/requests';
import { ClinicConditionSpotlight } from '../../types';

interface UpsertArgs {
  clinicConditionId: string;
  spotlight: ClinicConditionSpotlight;
}

interface DeleteArgs {
  clinicConditionId: string;
}

// Writes via the dedicated spotlight endpoint, then invalidates the clinic query so the editor +
// selector refetch in sync.
const useConditionSpotlight = (clinicId: string) => {
  const client = useQueryClient();

  const upsertMutation = useMutation({
    mutationFn: ({ clinicConditionId, spotlight }: UpsertArgs) =>
      clinicsApi.upsertConditionSpotlight(clinicId, clinicConditionId, spotlight),
    onSuccess: () => {
      client.invalidateQueries(['clinic', clinicId]);
      enqueueSnackbar('Spotlight saved', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to save spotlight', { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ clinicConditionId }: DeleteArgs) =>
      clinicsApi.deleteConditionSpotlight(clinicId, clinicConditionId),
    onSuccess: () => {
      client.invalidateQueries(['clinic', clinicId]);
      enqueueSnackbar('Spotlight removed', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to remove spotlight', { variant: 'error' });
    },
  });

  return {
    upsertSpotlight: upsertMutation.mutateAsync,
    deleteSpotlight: deleteMutation.mutateAsync,
    isSaving: upsertMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
  };
};

export default useConditionSpotlight;
